/* eslint-disable max-len */
import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import useUserContext from 'Context/UserContext';
import { sum } from 'Helpers/MapReduce';
import { planningUri, taskBoardUri } from 'Consts/advisorUris';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { ownerTypeHousehold } from 'Consts/Strings';
import { noop } from 'Helpers/PropTypes';
import './ActionItems.css';
import { pickImplementation } from 'Helpers/Affordances';
import relationships from 'Consts/Relationships';

function ActionItems({
  householdId,
  actionItems: actionItemsProp,
  user,
  toggle,
}) {
  const { fetchActionItemsByHouseholdID } = useSwaggerlessContext();
  const STATUS_NOT_APPLICABLE = 'not-applicable';
  const STATUS_COMPLETED = 'completed';
  const PRIORITY_HIGH = 'high';
  const PRIORITY_MEDIUM = 'medium';
  const PRIORITY_LOW = 'low';

  const { userResource } = useUserContext();
  const { householdResource } = useSimpleHousehold();

  const getPriorityCount = (items) => {
    return {
      low: items.filter(
        ({ attributes: { priority } }) => priority === PRIORITY_LOW,
      ).length,
      medium: items.filter(
        ({ attributes: { priority } }) => priority === PRIORITY_MEDIUM,
      ).length,
      high: items.filter(
        ({ attributes: { priority } }) => priority === PRIORITY_HIGH,
      ).length,
    };
  };

  const [actionItems, setActionItems] = useState(Array.isArray(actionItemsProp) ? actionItemsProp : []);

  const getActionItems = () => {
    if (householdId <= 0) {
      return null;
    }

    return fetchActionItemsByHouseholdID(householdId).then((res) => {
      if (res && res.data && Array.isArray(res.data)) {
        setActionItems(res.data);
      }
    });
  };


  useEffect(() => {
    getActionItems();
  }, [householdId]);

  const getAgentOutstandingDefault = (whose) => {
    if (whose === 'me') {
      return actionItems.filter(
        ({ attributes: { status, ownerId } }) => status !== STATUS_COMPLETED
          && status !== STATUS_NOT_APPLICABLE
          && ownerId === user.id.toString(),
      );
    }

    return actionItems.filter(
      ({ attributes: { status, ownerId, ownerType } }) => status !== STATUS_COMPLETED
        && status !== STATUS_NOT_APPLICABLE
        && ownerId !== user.id.toString()
        && ownerType !== ownerTypeHousehold,
    );
  };

  const getClientOutstandingDefault = () => {
    return actionItems.filter(
      ({ attributes: { status, ownerType } }) => status !== STATUS_COMPLETED
        && status !== STATUS_NOT_APPLICABLE
        && ownerType === ownerTypeHousehold,
    );
  };

  const getAgentOutstandingSimpleActionItems = (whose) => {
    if (whose === 'me') {
      return actionItems.filter(
        ({ attributes: { status, ownerId, workflowID } }) => status !== STATUS_COMPLETED
          && status !== STATUS_NOT_APPLICABLE
          && ownerId === user.id.toString()
          && !workflowID,
      );
    }

    return actionItems.filter(
      ({
        attributes: {
          status, ownerId, ownerType, workflowID,
        },
      }) => status !== STATUS_COMPLETED
        && status !== STATUS_NOT_APPLICABLE
        && ownerId !== user.id.toString()
        && ownerType !== ownerTypeHousehold
        && !workflowID,
    );
  };

  const getClientOutstandingSimpleActionItems = () => {
    return actionItems.filter(
      ({ attributes: { status, ownerType, workflowID } }) => status !== STATUS_COMPLETED
        && status !== STATUS_NOT_APPLICABLE
        && ownerType === ownerTypeHousehold
        && !workflowID,
    );
  };

  const getClientOutstanding = pickImplementation(userResource, {
    [relationships.simpleActionItems]: getClientOutstandingSimpleActionItems,
    [relationships.default]: getClientOutstandingDefault,
  });

  const getAgentOutstanding = pickImplementation(userResource, {
    [relationships.simpleActionItems]: getAgentOutstandingSimpleActionItems,
    [relationships.default]: getAgentOutstandingDefault,
  });

  const getOutstandingItemsLinksDefault = (outstanding, who) => {
    const item = getPriorityCount(outstanding);
    const total = Object.values(item).reduce(sum, 0);

    let totalItems = `${planningUri}?filterPriorityLow=true&filterPriorityMed=true&filterPriorityHigh=true&filterAssignedToClients=false&filterAssignedToMe=false`;

    if (who === 'me') {
      totalItems = `${planningUri}?filterPriorityLow=true&filterPriorityMed=true&filterPriorityHigh=true&filterAssignedToClients=false&filterAssignedToMe=false`;
    }

    if (who === 'household') {
      totalItems = `${planningUri}?filterPriorityLow=true&filterPriorityMed=true&filterPriorityHigh=true&filterAssignedToClients=false&filterAssignedToMe=false`;
    }

    let low = `${planningUri}?filterPriorityLow=true&householdId=${householdId}`;
    let med = `${planningUri}?filterPriorityMed=true&householdId=${householdId}`;
    let high = `${planningUri}?filterPriorityHigh=true&householdId=${householdId}`;

    if (total && who !== 'other') {
      totalItems += `&filterText=${outstanding[0].attributes.clientName}`;
    }

    if (total && who === 'other') {
      totalItems += `&filterText=${outstanding[0].attributes.clientName}`;
    }

    totalItems += `&householdId=${householdId}`;

    if (item.low) {
      low += `&filterText=${outstanding[0].attributes.ownerName}`;
    }
    if (item.med) {
      med += `&filterText=${outstanding[0].attributes.ownerName}`;
    }
    if (item.high) {
      high += `&filterText=${outstanding[0].attributes.ownerName}`;
    }

    return {
      totalItems,
      low,
      med,
      high,
    };
  };

  const getOutstandingItemsLinksSimpleActionItems = (outstanding, who) => {
    let actionItemRelationship = 'otherAgentTasks';

    if (who === 'me') {
      actionItemRelationship = 'myTasks';
    }

    if (who === 'household') {
      actionItemRelationship = 'householdTasks';
    }

    const totalItems = taskBoardUri({
      link: householdResource.relationship(actionItemRelationship).link('related').url(),
    });

    const low = taskBoardUri({
      link: householdResource.relationship(actionItemRelationship).link('related').url({
        params: {
          priority: ['low'],
        },
      }),
    });

    const med = taskBoardUri({
      link: householdResource.relationship(actionItemRelationship).link('related').url({
        params: {
          priority: ['medium'],
        },
      }),
    });

    const high = taskBoardUri({
      link: householdResource.relationship(actionItemRelationship).link('related').url({
        params: {
          priority: ['high'],
        },
      }),
    });

    return {
      totalItems,
      low,
      med,
      high,
    };
  };

  const getOutstandingItemsLinks = pickImplementation(userResource, {
    [relationships.simpleActionItems]: getOutstandingItemsLinksSimpleActionItems,
    [relationships.default]: getOutstandingItemsLinksDefault,
  });

  const renderOutstandingItem = (outstanding, who) => {
    const item = getPriorityCount(outstanding);
    const total = Object.values(item).reduce(sum, 0);

    const {
      totalItems,
      high,
      med,
      low,
    } = getOutstandingItemsLinks(outstanding, who);

    return (
      <div className="outstanding">
        <Link onClick={toggle} to={totalItems}>
          <div className="total">{total}</div>
        </Link>
        <Link onClick={toggle} to={low}>
          <div className="low">
            {item.low}
            <div className="badge">Low</div>
          </div>
        </Link>
        <Link onClick={toggle} to={med}>
          <div className="medium">
            {item.medium}
            <div className="badge">Med</div>
          </div>
        </Link>
        <Link onClick={toggle} to={high}>
          <div className="high">
            {item.high}
            <div className="badge">High</div>
          </div>
        </Link>
      </div>
    );
  };

  if (!householdId || !householdResource) {
    return <div>Loading...</div>;
  }

  const agentOutstanding = getAgentOutstanding('me');
  const otherOutstanding = getAgentOutstanding('other');
  const clientOutstanding = getClientOutstanding();

  return (
    <div className="landing-action-items">
      <div className="advisor-items">
          My Tasks
        {renderOutstandingItem(agentOutstanding, 'me')}
      </div>
      <div className="advisor-items">
          Other Agent
        {renderOutstandingItem(otherOutstanding, 'other')}
      </div>
      <div className="your-items">
          Client Tasks
        {renderOutstandingItem(clientOutstanding, 'household')}
      </div>
    </div>
  );
}

ActionItems.propTypes = {
  actionItems: PropTypes.object,
  householdId: PropTypes.number.isRequired,
  toggle: PropTypes.func,
  user: PropTypes.object.isRequired,
};

ActionItems.defaultProps = {
  toggle: noop,
  actionItems: {
    attributes: {},
  },
};

const withHouseholdContext = Wrapped => (props) => {
  const householdContext = useSimpleHousehold();

  const { household } = householdContext;

  const { id: householdId } = household;

  const { user } = useUserContext();

  const hhProps = {
    householdId,
    user,
  };

  return <Wrapped {...props} {...hhProps} />;
};

const ActionItemsContainer = withHouseholdContext(ActionItems);

export default ActionItemsContainer;
